import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Save, RefreshCw, X, Mail, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ASTRO_API } from "@/lib/api";

const PIN_KEY = "astrodev_admin_pin";

const makeAdminClient = (pin) =>
  axios.create({
    baseURL: ASTRO_API,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Pin": pin,
    },
  });

const Admin = () => {
  const { t, lang } = useLang();
  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_KEY) || "");
  const [loggedIn, setLoggedIn] = useState(!!sessionStorage.getItem(PIN_KEY));
  const [loginErr, setLoginErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [settingsMsg, setSettingsMsg] = useState(null);

  const client = makeAdminClient(pin);

  const login = async (e) => {
    e.preventDefault();
    setLoginErr(null);
    setBusy(true);
    try {
      const c = makeAdminClient(pin);
      await c.post("/admin/verify", {});
      sessionStorage.setItem(PIN_KEY, pin);
      setLoggedIn(true);
    } catch (e2) {
      setLoginErr(t.invalidPin);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PIN_KEY);
    setPin("");
    setLoggedIn(false);
    setBookings([]);
    setFeedback([]);
    setSettings(null);
  };

  const loadAll = async () => {
    try {
      const [b, f, s] = await Promise.all([
        client.get("/admin/bookings"),
        client.get("/admin/feedback"),
        client.get("/settings"),
      ]);
      setBookings(b.data || []);
      setFeedback(f.data || []);
      setSettings(s.data || {});
    } catch (e2) {
      if (e2?.response?.status === 401) {
        logout();
      }
    }
  };

  useEffect(() => {
    if (loggedIn) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  const saveSettings = async () => {
    setSettingsMsg(null);
    try {
      await client.post("/admin/settings", settings);
      setSettingsMsg({ type: "ok", text: t.saved });
    } catch (e) {
      setSettingsMsg({
        type: "err",
        text: e?.response?.data?.detail || "Error",
      });
    }
  };

  if (!loggedIn) {
    return (
      <div
        className={`section ${lang === "hi" ? "font-devnagri" : ""}`}
        data-testid="admin-login-page"
      >
        <div className="container-narrow max-w-md">
          <div className="card-glass p-8">
            <div className="mx-auto w-14 h-14 mb-5 rounded-full flex items-center justify-center border border-[rgba(212,166,74,0.5)]">
              <Lock className="text-[color:var(--gold)]" />
            </div>
            <h1 className="font-display text-3xl gold-text text-center mb-6">
              {t.adminLoginTitle}
            </h1>
            <form onSubmit={login} className="space-y-4">
              <div className="field">
                <label>{t.adminPin}</label>
                <input
                  type="password"
                  className="input tracking-widest text-center"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  required
                  inputMode="numeric"
                  autoFocus
                  data-testid="admin-pin-input"
                />
              </div>
              {loginErr && (
                <div className="notice error" data-testid="admin-login-error">
                  {loginErr}
                </div>
              )}
              <button
                type="submit"
                className="btn-gold w-full"
                disabled={busy}
                data-testid="admin-login-btn"
              >
                {busy ? "..." : t.login}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const unread = bookings.filter((b) => !b.is_read).length;

  return (
    <div
      className={`section ${lang === "hi" ? "font-devnagri" : ""}`}
      data-testid="admin-panel"
    >
      <div className="container-narrow">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <span className="gold-divider mb-2">Admin</span>
            <h1 className="font-display text-3xl md:text-4xl gold-text">
              {t.adminPanelTitle}
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadAll}
              className="btn-ghost text-sm py-2 px-3"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={logout}
              className="btn-ghost text-sm py-2 px-4"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> {t.logout}
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "bookings", label: `${t.bookings} (${bookings.length}${unread ? ` · ${unread} ${t.unread}` : ""})` },
            { id: "feedback", label: `${t.testimonialsTitle} (${feedback.length})` },
            { id: "settings", label: t.settings },
          ].map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`px-4 py-2 rounded-full font-sans-ui text-sm border transition ${
                tab === tb.id
                  ? "bg-[color:var(--gold)] text-[color:var(--maroon-darker)] border-[color:var(--gold)]"
                  : "text-[color:var(--gold-soft)] border-[rgba(212,166,74,0.35)] hover:border-[color:var(--gold)]"
              }`}
              data-testid={`admin-tab-${tb.id}`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {/* Bookings */}
        {tab === "bookings" && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            {bookings.length === 0 ? (
              <div className="notice">{t.noBookings}</div>
            ) : (
              bookings.map((b) => (
                <div
                  key={b.id}
                  className="card-glass p-4 md:p-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between"
                  data-testid={`admin-booking-${b.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                          b.is_read
                            ? "text-[color:var(--gold-soft)] border border-[rgba(212,166,74,0.3)]"
                            : "bg-[color:var(--gold)] text-[color:var(--maroon-darker)] font-bold"
                        }`}
                      >
                        {b.is_read ? t.read : t.unread}
                      </span>
                      <span className="text-xs text-[color:var(--gold-soft)] font-sans-ui">
                        {new Date(b.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="font-display text-lg" style={{ color: "#f6ecd6" }}>
                      {b.full_name}{" "}
                      <span className="text-[color:var(--gold-soft)] text-sm font-sans-ui">
                        · {b.package_label} · ₹{b.package_amount}
                      </span>
                    </div>
                    <div className="text-sm text-[color:var(--cream)] opacity-85 font-sans-ui truncate">
                      <Phone size={12} className="inline mr-1" />
                      {b.contact}
                      {b.email ? (
                        <>
                          {" · "}
                          <Mail size={12} className="inline mr-1" />
                          {b.email}
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`https://wa.me/${(b.contact || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost text-xs py-2 px-3"
                      data-testid={`admin-wa-${b.id}`}
                    >
                      WhatsApp
                    </a>
                    <button
                      onClick={() => setSelected(b)}
                      className="btn-gold text-xs py-2 px-3"
                      data-testid={`admin-view-${b.id}`}
                    >
                      {t.viewDetails}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Feedback */}
        {tab === "feedback" && (
          <div
            className="grid md:grid-cols-2 gap-4"
            data-testid="admin-feedback-list"
          >
            {feedback.length === 0 ? (
              <div className="notice">—</div>
            ) : (
              feedback.map((f) => (
                <div
                  key={f.id}
                  className="card-glass p-5"
                  data-testid={`admin-feedback-${f.id}`}
                >
                  <div className="text-xs text-[color:var(--gold-soft)] mb-2 font-sans-ui">
                    {new Date(f.created_at).toLocaleString()}
                  </div>
                  <p className="text-[color:var(--cream)] opacity-90 mb-3">
                    “{f.text}”
                  </p>
                  <div className="text-sm text-[color:var(--gold-soft)] font-sans-ui">
                    — {f.name}
                    {f.city ? `, ${f.city}` : ""}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Settings */}
        {tab === "settings" && settings && (
          <div className="card-glass p-6 md:p-8 space-y-5" data-testid="admin-settings-panel">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="field">
                <label>WhatsApp Number</label>
                <input
                  className="input"
                  value={settings.whatsapp_number || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, whatsapp_number: e.target.value })
                  }
                  data-testid="settings-whatsapp"
                />
              </div>
              <div className="field">
                <label>Inquiry Number</label>
                <input
                  className="input"
                  value={settings.inquiry_number || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, inquiry_number: e.target.value })
                  }
                  data-testid="settings-inquiry"
                />
              </div>
              <div className="field">
                <label>UPI Number</label>
                <input
                  className="input"
                  value={settings.upi_number || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, upi_number: e.target.value })
                  }
                  data-testid="settings-upi"
                />
              </div>
              <div className="field">
                <label>UPI Name</label>
                <input
                  className="input"
                  value={settings.upi_name || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, upi_name: e.target.value })
                  }
                  data-testid="settings-upi-name"
                />
              </div>
              <div className="field md:col-span-2">
                <label>Office Address (English)</label>
                <textarea
                  className="textarea"
                  value={settings.office_address_en || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, office_address_en: e.target.value })
                  }
                  data-testid="settings-address-en"
                />
              </div>
              <div className="field md:col-span-2">
                <label>कार्यालय का पता (हिन्दी)</label>
                <textarea
                  className="textarea font-devnagri"
                  value={settings.office_address_hi || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, office_address_hi: e.target.value })
                  }
                  data-testid="settings-address-hi"
                />
              </div>
              <div className="field md:col-span-2">
                <label>Google Maps URL (share link)</label>
                <input
                  className="input"
                  value={settings.google_maps_url || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, google_maps_url: e.target.value })
                  }
                  placeholder="https://share.google/…"
                  data-testid="settings-maps-url"
                />
              </div>
              <label className="flex items-center gap-2 font-sans-ui text-sm text-[color:var(--cream)]">
                <input
                  type="checkbox"
                  checked={!!settings.feedback_enabled}
                  onChange={(e) =>
                    setSettings({ ...settings, feedback_enabled: e.target.checked })
                  }
                  data-testid="settings-feedback-enabled"
                />
                Feedback form enabled on public site
              </label>
            </div>

            {settingsMsg && (
              <div
                className={`notice ${settingsMsg.type === "err" ? "error" : ""}`}
                data-testid="settings-message"
              >
                {settingsMsg.text}
              </div>
            )}

            <button
              onClick={saveSettings}
              className="btn-gold"
              data-testid="settings-save-btn"
            >
              <Save size={14} /> {t.save}
            </button>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10,2,6,0.75)" }}
          onClick={() => setSelected(null)}
          data-testid="admin-booking-modal"
        >
          <div
            className="card-glass max-w-lg w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-3 right-3 text-[color:var(--gold-soft)] hover:text-[color:var(--gold)]"
              data-testid="admin-modal-close"
            >
              <X />
            </button>
            <div className="text-xs uppercase tracking-widest text-[color:var(--gold-soft)] mb-1">
              {selected.package_label} · ₹{selected.package_amount}
            </div>
            <h3 className="font-display text-2xl mb-4" style={{ color: "#f6ecd6" }}>
              {selected.full_name}
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm font-sans-ui text-[color:var(--cream)] opacity-90">
              <Info k="Gender" v={selected.gender} />
              <Info k="Contact" v={selected.contact} />
              <Info k="Email" v={selected.email || "—"} />
              <Info k="DOB" v={selected.dob} />
              <Info k="Time" v={selected.time_of_birth} />
              <Info k="Place" v={selected.place_of_birth} />
              <Info k="1st Child DOB" v={selected.first_child_dob || "—"} />
              <Info k="1st Child Time" v={selected.first_child_tob || "—"} />
              <Info k="2nd Child DOB" v={selected.second_child_dob || "—"} />
              <Info k="2nd Child Time" v={selected.second_child_tob || "—"} />
              <Info k="Txn Ref" v={selected.upi_txn_ref || "—"} />
              <Info k="Status" v={selected.status || "—"} />
            </div>
            {selected.questions && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--gold-soft)] mb-1">
                  Questions
                </div>
                <p className="text-[color:var(--cream)] opacity-90 whitespace-pre-wrap">
                  {selected.questions}
                </p>
              </div>
            )}
            {selected.life_events && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--gold-soft)] mb-1">
                  Life Events
                </div>
                <p className="text-[color:var(--cream)] opacity-90 whitespace-pre-wrap">
                  {selected.life_events}
                </p>
              </div>
            )}
            {selected.payment_screenshot_base64 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--gold-soft)] mb-2">
                  Payment Screenshot
                </div>
                <img
                  src={selected.payment_screenshot_base64}
                  alt="payment"
                  className="rounded-lg border border-[rgba(212,166,74,0.35)] max-h-72"
                />
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <a
                href={`https://wa.me/${(selected.contact || "").replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="btn-gold text-sm py-2 px-4"
                data-testid="admin-modal-wa"
              >
                WhatsApp
              </a>
              {selected.email && (
                <a
                  href={`mailto:${selected.email}`}
                  className="btn-ghost text-sm py-2 px-4"
                  data-testid="admin-modal-mail"
                >
                  Email
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-[color:var(--gold-soft)]">
      {k}
    </div>
    <div>{v}</div>
  </div>
);

export default Admin;
